import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CustomDatePicker = ({ onDateSelect, initialDate, onCancel }) => {
  const [currentMonth, setCurrentMonth] = useState(initialDate || new Date());
  const [selectedDate, setSelectedDate] = useState(initialDate || new Date());

  // Normalize dates to midnight for accurate comparisons
  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  useEffect(() => {
    const today = normalizeDate(new Date());

    if (initialDate) {
      const initial = normalizeDate(new Date(initialDate));
      if (initial < today) {
        setSelectedDate(today);
        setCurrentMonth(today);
        return;
      }
      setSelectedDate(initial);
      setCurrentMonth(initial);
    } else {
      setSelectedDate(today);
      setCurrentMonth(today);
    }
  }, [initialDate]);

  const formatDate = (date) => {
    return new Intl.DateTimeFormat("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const formatMonthYear = (date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      year: "numeric",
    }).format(date);
  };

  const handleDayClick = (day) => {
    const normalizedDay = normalizeDate(day);
    if (isDateDisabled(normalizedDay)) return;
    setSelectedDate(normalizedDay);
  };

  const handlePrevMonth = () => {
    setCurrentMonth((prevMonth) => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(prevMonth.getMonth() - 1);
      return newMonth;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth((prevMonth) => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(prevMonth.getMonth() + 1);
      return newMonth;
    });
  };

  const generateCalendarDays = () => {
    const days = [];
    const firstDayOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    );
    const lastDayOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    );

    // Get the day of week for the first day (0 = Sunday, 6 = Saturday)
    const firstDayOfWeek = firstDayOfMonth.getDay();

    // Add days from previous month to fill the first week
    const prevMonthLastDay = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      0
    ).getDate();

    // Sunday is 0 in JS, but we want Monday to be first (0)
    const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    for (let i = startOffset; i > 0; i--) {
      const day = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        prevMonthLastDay - i + 1
      );
      days.push({ date: day, isCurrentMonth: false });
    }

    // Add days of current month
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const day = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        i
      );
      days.push({ date: day, isCurrentMonth: true });
    }

    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 rows x 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const day = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        i
      );
      days.push({ date: day, isCurrentMonth: false });
    }

    return days;
  };

  const isSelectedDate = (date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleApply = () => {
    onDateSelect(selectedDate);
  };

  const calendarDays = generateCalendarDays();

  // Date comparison with normalized dates
  const isDateDisabled = (date) => {
    const today = normalizeDate(new Date());
    return normalizeDate(date) < today;
  };

  return (
    <div className="bg-n100 rounded-lg shadow-xl w-full max-w-md overflow-hidden">
      {/* Header with selected date */}
      <div className="p-4 text-center border-b border-n200">
        <div className="text-p300 text-sm">Selected Date</div>
        <div className="font-medium text-p300">{formatDate(selectedDate)}</div>
      </div>

      {/* Calendar header */}
      <div className="flex items-center justify-between p-4">
        <button
          onClick={handlePrevMonth}
          className="p-2 rounded-full hover:bg-n200 text-p300">
          <ChevronLeft size={20} />
        </button>
        <div className="text-lg font-medium text-p300">
          {formatMonthYear(currentMonth)}
        </div>
        <button
          onClick={handleNextMonth}
          className="p-2 rounded-full hover:bg-n200 text-p300">
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Calendar grid */}
      <div className="p-2">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 mb-1">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div
              key={day}
              className="text-center text-xs font-medium py-2 text-p300">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((dayInfo, index) => {
            const { date, isCurrentMonth } = dayInfo;
            const normalizedDate = normalizeDate(date);
            const disabled = isDateDisabled(normalizedDate) || !isCurrentMonth;
            const isSelected =
              normalizeDate(selectedDate).getTime() ===
              normalizedDate.getTime();

            let dayClasses =
              "w-8 h-8 flex items-center justify-center rounded-full mx-auto";

            if (disabled) {
              dayClasses += " text-n300 cursor-not-allowed opacity-50";
            } else if (isSelected) {
              dayClasses += " bg-p300 text-p400";
            } else if (
              normalizeDate(new Date()).getTime() === normalizedDate.getTime()
            ) {
              dayClasses += " border-2 border-p300 text-p300";
            } else {
              dayClasses += " text-p300 hover:bg-n200";
            }

            return (
              <div key={index} className="py-1 relative">
                <button
                  className={dayClasses}
                  onClick={() => handleDayClick(normalizedDate)}
                  disabled={disabled}>
                  {date.getDate()}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 flex space-x-2 border-t border-n200">
        <button
          onClick={onCancel}
          className="w-full py-2 px-4 bg-p400 border border-n200 rounded text-p300 font-medium text-xs">
          Cancel
        </button>
        <button
          onClick={handleApply}
          className="w-full py-2 px-4 bg-swipe-button text-p400 rounded font-medium text-xs">
          Apply
        </button>
      </div>
    </div>
  );
};

export default CustomDatePicker;
